import { DOCUMENT } from '@angular/common';
import { HttpClient } from '@angular/common/http';
import { Inject, Injectable } from '@angular/core';
import { CookieService } from 'ngx-cookie-service';
import { LoggerService } from '@wdpr/ra-angular-logger';

import { ConfigService } from '@finder/core/config.service';
import { CONFIG_CONSTANTS, FINDER_TOGGLES_COOKIE_NAME } from '@finder/shared/constants/app.constants';
import { Subject } from 'rxjs';
import { EditorData } from '@finder/features/debug/services/debug-service/debug.interface';

@Injectable({
    providedIn: 'root',
})
export class DebugService {
    get editorData$() {
        return this.editorData.asObservable();
    }

    private editorData = new Subject<EditorData>();

    constructor(
        private http: HttpClient,
        private config: ConfigService,
        private cookieService: CookieService,
        private logger: LoggerService,
        @Inject(DOCUMENT) private document: Document,
    ) { }

    isDebugEnabled(): boolean {
        let enabled = false;
        const cookie = this.cookieService.get(FINDER_TOGGLES_COOKIE_NAME);

        try {
            const cookieValue: any = JSON.parse(cookie);
            enabled = cookieValue.debug === 'true' || cookieValue.debug === true;
        } catch (e) {
            this.logger.warn('Unable to parse debug cookie!');
        }

        return enabled;
    }

    getUiGitLog() {
        return this.http.get(`/advanced-finder/content/git.log`, { responseType: 'text' });
    }

    getApiGitLog() {
        return this.http.get(`${this.getApiBase()}/debug-service/git.log`, { responseType: 'text' });
    }

    getAllowedCookiesOnServer() {
        return this.http.get(`${this.getApiBase()}/debug-service/cookies/allowed-cookies`);
    }

    parseGitLog(log, type) {
        // Split log
        log = log.split('\n');

        // Process each log line
        log = log.map(line => {
            // Strip tag info
            line = line.replace(/- \([^\)]+\)/, '- ')
                .replace(/\s{2,}/, ' ');

            // Fix angle brackets (around names)
            // Make sure adding any links happens AFTER this
            line = line.replace('<', '&lt;').replace('>', '&gt;');

            // Remove time
            line = line.replace(/\(\d+ \b\w+\b ago\)/i, '')
                .replace(/\s{2,}/, ' ');

            // Add JIRA link
            line = this.getJiraLink(line);

            // Add pull link
            line = this.getGithubPullLink(line, type);

            // Add commit link
            line = this.getGithubCommitLink(line, type);

            return line;
        });

        return log;
    }

    getUiDependencies() {
        return this.http.get(`/advanced-finder/content/dependencies.json`, { responseType: 'json' });
    }

    getApiDependencies() {
        return this.http.get(`${this.getApiBase()}/debug-service/dependencies.json`, { responseType: 'json' });
    }

    updateEditorData(data: EditorData) {
        this.editorData.next(data);
    }

    private getJiraLink(line: string) {
        const regex = /[\[]*git[\s|-]\d{1,}[\]]*/i;
        const match = line.match(regex);
        if (match) {
            // Format the number
            const num = match[0]
                .toUpperCase()
                .replace(' ', '-')
                .replace(/[\[\]]/g, '');

            // Create anchor
            line = this.getAnchor(
                line,
                match,
                `[${num}]`,
                `https://myjira.disney.com/browse/${num}`
            );
        }

        return line;
    }

    private getGithubPullLink(line: string, type: string) {
        const regex = /\(#(\d*)\)/;
        const match = line.match(regex);
        if (match) {
            // Format the number
            const num = match[0]
                .replace(/[#\(\)]/g, '');

            // Create anchor
            line = this.getAnchor(
                line,
                match,
                `(#${num})`,
                `https://github.disney.com/wdpro-development/wdpr-content-advfinder-${type}/pull/${num}`
            );
        }

        return line;
    }

    private getGithubCommitLink(line: string, type: string) {
        const regex = /^\* \w+/;
        const match = line.match(regex);
        if (match) {
            // Format the number
            const num = match[0]
                .replace('* ', '');

            // Create anchor
            line = this.getAnchor(
                line,
                match,
                num,
                `https://github.disney.com/wdpro-development/wdpr-content-advfinder-${type}/commit/${num}`
            );
        }

        return line;
    }

    private getAnchor(line, match, name, url) {
        // Create anchor
        const a = this.document.createElement('a');
        a.href = url;
        a.text = name;
        a.target = '_blank';

        // Replace
        return line.replace(match[0], a.outerHTML);
    }

    private getApiBase() {
        const baseUrl = this.config.getValue(CONFIG_CONSTANTS.apiUrlKey);
        const version = this.config.getValue(CONFIG_CONSTANTS.defaultVersionKey);

        return `${baseUrl}/${version}`;
    }
}
